export interface PlatformConfig {
  [key: string]: string | number | boolean | null | undefined
}

export interface LaunchOptions {
  skipReview?: boolean
  testMode?: boolean
  notifyOnLaunch?: boolean
}

export interface RadiusTargeting {
  latitude: number
  longitude: number
  radius: number
  unit: "mile" | "kilometer"
}

export interface DaypartSchedule {
  days: string[]
  startHour: number
  endHour: number
}

// Campaign Types
export enum CampaignStatus {
  ACTIVE = "ACTIVE",
  PAUSED = "PAUSED",
  DELETED = "DELETED",
  ARCHIVED = "ARCHIVED",
}

export enum CampaignObjective {
  AWARENESS = "AWARENESS",
  TRAFFIC = "TRAFFIC",
  ENGAGEMENT = "ENGAGEMENT",
  LEADS = "LEADS",
  APP_PROMOTION = "APP_PROMOTION",
  SALES = "SALES",
}

export enum BudgetType {
  DAILY = "DAILY",
  LIFETIME = "LIFETIME",
}

// Meta Campaign Response Interface (matches Meta API + service processing)
export interface Campaign {
  id: string
  name: string
  status: string // Meta uses different status values than our enum
  objective: string // Meta uses different objective values
  budget_type?: "DAILY" | "LIFETIME" // Added by our service
  budget_level?: "CAMPAIGN" | "ADSET" // Added by our service
  daily_budget?: string // From Meta API (in cents as string)
  lifetime_budget?: string // From Meta API (in cents as string)
  budget_remaining?: string // From Meta API
  start_time?: string // Meta field name
  stop_time?: string // Meta field name
  created_time: string // Meta field name
  updated_time: string // Meta field name
  effective_status?: string
  configured_status?: string
  account_id?: string
}

export interface CreateCampaignDto {
  name: string
  objective: CampaignObjective
  budgetType: BudgetType
  budgetAmount: number
  startDate?: string
  endDate?: string
  platformConfig?: PlatformConfig
}

export interface UpdateCampaignDto {
  name?: string
  objective?: CampaignObjective
  budgetType?: BudgetType
  budgetAmount?: number
  startDate?: string
  endDate?: string
  platformConfig?: PlatformConfig
}

export interface UpdateCampaignBudgetDto {
  budgetType: BudgetType
  budgetAmount: number
}

export interface UpdateCampaignStatusDto {
  status: CampaignStatus
}

// AdSet Types
export enum AdSetStatus {
  ACTIVE = "ACTIVE",
  PAUSED = "PAUSED",
  DELETED = "DELETED",
  ARCHIVED = "ARCHIVED",
}

export enum BillingEvent {
  IMPRESSIONS = "IMPRESSIONS",
  CLICKS = "CLICKS",
  CONVERSIONS = "CONVERSIONS",
  VIDEO_VIEWS = "VIDEO_VIEWS",
}

export enum OptimizationGoal {
  REACH = "REACH",
  IMPRESSIONS = "IMPRESSIONS",
  LINK_CLICKS = "LINK_CLICKS",
  LANDING_PAGE_VIEWS = "LANDING_PAGE_VIEWS",
  CONVERSIONS = "CONVERSIONS",
  VALUE = "VALUE",
  LEAD_GENERATION = "LEAD_GENERATION",
}

export interface GeoTargeting {
  countries?: string[]
  regions?: string[]
  cities?: string[]
  postalCodes?: string[]
  radius?: RadiusTargeting
}

export interface DemographicTargeting {
  minAge?: number
  maxAge?: number
  genders?: string[]
  languages?: string[]
}

export interface InterestTargeting {
  interests?: string[]
  behaviors?: string[]
  customAudiences?: string[]
  lookalikeAudiences?: string[]
}

export interface DeviceTargeting {
  deviceTypes?: string[]
  operatingSystems?: string[]
  connectionTypes?: string[]
}

export interface Targeting {
  geo?: GeoTargeting
  demographic?: DemographicTargeting
  interest?: InterestTargeting
  device?: DeviceTargeting
  platformSpecific?: PlatformConfig
}

export interface AdSet {
  id: string
  name: string
  status: string
  campaign_id: string
  daily_budget?: string
  lifetime_budget?: string
  budget_remaining?: string
  start_time?: string
  end_time?: string
  billing_event?: string
  optimization_goal?: string
  destination_type?: string
  targeting?: Record<string, unknown> // Meta targeting structure is complex
  created_time: string
  updated_time: string
  effective_status?: string
  configured_status?: string

  // Fields added by our backend service
  budget_type?: "DAILY" | "LIFETIME"
  budget_amount?: string
  budget_level?: "CAMPAIGN" | "ADSET" | "UNKNOWN"
  budget_note?: string
  campaign_budget?: string
  campaign?: Pick<Campaign, "id" | "name" | "status" | "budget_type"> // Campaign info when budget is at campaign level
}

export interface CreateAdSetDto {
  name: string
  dailyBudget: number
  lifetimeBudget?: number
  billingEvent: BillingEvent
  optimizationGoal: OptimizationGoal
  bidAmount?: number
  targeting: Targeting
  startDate?: string
  endDate?: string
  schedule?: DaypartSchedule[]
  platformConfig?: PlatformConfig
}

export interface UpdateAdSetDto {
  name?: string
  billingEvent?: BillingEvent
  optimizationGoal?: OptimizationGoal
  bidAmount?: number
  startDate?: string
  endDate?: string
  schedule?: DaypartSchedule[]
  platformConfig?: PlatformConfig
}

export interface UpdateAdSetBudgetDto {
  dailyBudget?: number
  lifetimeBudget?: number
}

export interface UpdateAdSetTargetingDto {
  targeting: Targeting
}

export interface UpdateAdSetStatusDto {
  status: AdSetStatus
}

// Ad Types
export enum AdStatus {
  ACTIVE = "ACTIVE",
  PAUSED = "PAUSED",
  DELETED = "DELETED",
  ARCHIVED = "ARCHIVED",
  PENDING_REVIEW = "PENDING_REVIEW",
  DISAPPROVED = "DISAPPROVED",
}

export enum AdFormat {
  SINGLE_IMAGE = "SINGLE_IMAGE",
  SINGLE_VIDEO = "SINGLE_VIDEO",
  CAROUSEL = "CAROUSEL",
  COLLECTION = "COLLECTION",
  SLIDESHOW = "SLIDESHOW",
  PLAYABLE = "PLAYABLE",
}

export enum CallToAction {
  APPLY_NOW = "APPLY_NOW",
  BOOK_NOW = "BOOK_NOW",
  CONTACT_US = "CONTACT_US",
  DOWNLOAD = "DOWNLOAD",
  GET_OFFER = "GET_OFFER",
  GET_QUOTE = "GET_QUOTE",
  INSTALL_NOW = "INSTALL_NOW",
  LEARN_MORE = "LEARN_MORE",
  LISTEN_NOW = "LISTEN_NOW",
  PLAY_GAME = "PLAY_GAME",
  SEE_MORE = "SEE_MORE",
  SHOP_NOW = "SHOP_NOW",
  SIGN_UP = "SIGN_UP",
  SUBSCRIBE = "SUBSCRIBE",
  USE_APP = "USE_APP",
  VIEW_INSTAGRAM_PROFILE = "VIEW_INSTAGRAM_PROFILE",
  WATCH_MORE = "WATCH_MORE",
  WATCH_NOW = "WATCH_NOW",
}

export interface MediaAsset {
  type: "image" | "video"
  url: string
  assetId?: string
  thumbnailUrl?: string
  caption?: string
  link?: string
}

export interface AdCreative {
  primaryText: string
  headline?: string
  description?: string
  /**
   * Optional. Leave unset for Profile Visit / INSTAGRAM_PROFILE ad sets — the
   * backend auto-resolves it to the connected Instagram profile URL.
   */
  destinationUrl?: string
  displayLink?: string
  callToAction: CallToAction
  mediaAssets: MediaAsset[]
  platformSpecific?: PlatformConfig
}

export interface TrackingSpec {
  urlParameters?: Record<string, string>
  pixelId?: string
  conversionIds?: string[]
}

export interface Ad {
  id: string
  name: string
  status: AdStatus
  adsetId: string
  format: AdFormat
  creative: AdCreative
  tracking?: TrackingSpec
  platformConfig?: PlatformConfig
  createdAt: string
  updatedAt: string
}

export interface CreateAdDto {
  name: string
  format: AdFormat
  creative: AdCreative
  tracking?: TrackingSpec
  platformConfig?: PlatformConfig
}

export interface UpdateAdDto {
  name?: string
  creative?: AdCreative
  tracking?: TrackingSpec
  platformConfig?: PlatformConfig
}

export interface UpdateAdStatusDto {
  status: AdStatus
}

export interface LaunchAdDto {
  scheduledTime?: string
  launchOptions?: LaunchOptions
}

// API Response types
export interface PaidMediaApiResponse<T> {
  data: T
  success: boolean
  message?: string
}

export type SupportedPlatform = "meta" | "tiktok" | "google"

// Apply URL Tags
export interface ApplyUrlTagsItem {
  adId: string
}

export interface ApplyUrlTagsRequest {
  ads: ApplyUrlTagsItem[]
  urlTags: string
}

export interface ApplyUrlTagsResult {
  adId: string
  success: boolean
  error?: string
}

export interface ApplyUrlTagsResponse {
  results: ApplyUrlTagsResult[]
  summary: { total: number; successful: number; failed: number }
  operationId: string
}

// Apply Exclusions
export interface ApplyExclusionsItem {
  adsetId: string
  audienceIds: string[]
}

export interface ApplyExclusionsRequest {
  adsets: ApplyExclusionsItem[]
}

export interface ApplyExclusionsResult {
  adsetId: string
  success: boolean
  addedCount?: number
  error?: string
}

export interface ApplyExclusionsResponse {
  results: ApplyExclusionsResult[]
  summary: {
    total: number
    successful: number
    failed: number
  }
  operationId: string
}

/**
 * GET /paid-media/meta/adsets/:adSetId/creative-test/eligibility — live
 * pre-flight for a Meta ad-level creative test (MD-5569). `reasons` are hard
 * blockers, `warnings` are notes; budgets are in the account's minor unit.
 */
export interface CreativeTestEligibility {
  eligible: boolean
  reasons: string[]
  warnings: string[]
  adSet: {
    id: string
    name: string
    status: string
    effectiveStatus?: string
    optimizationGoal?: string
    campaignId: string
    campaignName?: string
    budgetLevel: "ADSET" | "CAMPAIGN"
    bidStrategy?: string
    dailyBudget: number | null
    lifetimeBudget: number | null
  }
  activeStudies: {
    id: string
    name: string
    type: string
    startTime?: string
    endTime?: string
  }[]
  /** Account currency major units; null when insights were unavailable. */
  spendLast7d: number | null
  /** 20% of the daily budget, cents; null on lifetime budgets. */
  suggestedDailyBudgetCents: number | null
}

/** GET /paid-media/meta/creative-tests/:studyId — the study, its cells and Meta's outcome. */
export interface CreativeTestView {
  id: string
  name: string
  description?: string
  type: string
  status: "SCHEDULED" | "RUNNING" | "ENDED" | "CANCELED"
  startTime?: string
  endTime?: string
  canceledTime?: string
  resultsFirstAvailableDate?: string
  cells: {
    id: string
    name: string
    treatmentPercentage?: number
    adIds: string[]
  }[]
  results: {
    highPerformerAdIds: string[]
    confidenceByAdId: Record<string, number>
    updatedAt?: string
  } | null
}
